import { useState, useEffect } from "react";
import {
  validateRequired,
  validateFileType,
  validateFileSize,
  ErrorMessage,
  validateImageFile,
} from "../../utlis/function";
import { callApi } from "../../services/ApiServices";
import { useGlobalStates } from "../../utlis/ClubsContext";
import CommonLoader from "../common/CommonLoader";

const EditBasicInfoModal = ({ isOpen, onClose, initialData }) => {
  const [formData, setFormData] = useState({
    clubName: "",
    description: "",
    clubTagline: "",
    logo: null,
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [logoLoading, setLogoLoading] = useState(false);
  const { selectedClub, handleSelectedClubDetail } = useGlobalStates();

  // Reset form when modal opens/closes or initialData changes
  useEffect(() => {
    if (isOpen) {
      setFormData({
        clubName: initialData?.clubName || "",
        description: initialData?.description || "",
        clubTagline: initialData?.clubTagline || "",
        logo: initialData?.logo || null,
      });
      setErrors({});
      setIsSubmitting(false);
    }
  }, [isOpen, initialData]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields
    const newErrors = {};

    if (!validateRequired(formData.clubName)) {
      newErrors.clubName = "Club name is required";
    }

    if (!validateRequired(formData.description)) {
      newErrors.description = "Description is required";
    }else if(formData.description.length > 500) {
      newErrors.description = "Description must not exceed 500 characters."
    }

    if(!validateRequired(formData.clubTagline)) {
      newErrors.clubTagline = "Club tagline is required";
    }

    setErrors(newErrors);

    // If there are errors, don't submit
    if (Object.keys(newErrors).length > 0) {
      return;
    }

    const isFormChanged = () => {
      return (
        formData.clubName !== initialData?.clubName ||
        formData.description !== initialData?.description ||
        formData?.logo?.id !== initialData?.logo?.id ||
        formData?.clubTagline !== initialData?.clubTagline
      );
    };
    if (!isFormChanged()) {
      onClose();
      return;
    }

    setIsSubmitting(true);

    try {
      // Simulate API call
      const fields = {};

      if (formData?.clubName) fields.clubName = formData.clubName;
      if (formData?.description) fields.description = formData.description;
      if (formData?.logo?.id) fields.clubLogo = formData.logo.id;
      if(formData?.clubTagline) fields.clubTagline = formData.clubTagline;
      const response = await callApi({
        method: "put",
        url: `club/update/${selectedClub?.id}`,
        data: { fields },
      });
      if (response.status === 200) {
        handleSelectedClubDetail(selectedClub);
        onClose();
      }
    } catch (error) {
      console.error("Submission error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLogoChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setErrors((prev) => ({
      ...prev,
      logo: null,
    }));

    const error = validateImageFile(file, 5);
    if (error) {
      setErrors((prev) => ({ ...prev, logo: error }));
      return;
    }

    try {
      setLogoLoading(true);
      const payload = new FormData();
      payload.append("file", file);
      const response = await callApi({
        method: "post",
        url: "file/upload",
        data: payload,
      });

      setFormData((prev) => ({ ...prev, logo: response }));
    } catch (error) {
      console.log("error while upload the logo image", error);
    } finally {
      setLogoLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value });

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors({ ...errors, [field]: "" });
    }
  };

  if (!isOpen) return null;

const hasErrors = Object.values(errors).some(
  (value) => value && value.trim() !== ""
);

  console.log("initialData", initialData);
  console.log("isSubmitting", isSubmitting);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      {isSubmitting && <CommonLoader fullScreen />}
      <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto main-shadow">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 ">
          <h2 className="text-xl md:text-3xl font-medium text-black tracking-[0] leading-[normal]">
            Basic Information
          </h2>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors"
            >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M15 5L5 15M5 5L15 15"
                stroke="#4C4C4C"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                />
            </svg>
          </button>
        </div>
                <p className="px-6 text-xl text-textcolor">Add core details to identify and describe this club on the DRS platform.</p>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Logo Upload */}
          <div>
            <label className="block text-base font-medium text-black tracking-[0] leading-[normal] mb-3">
              Upload Logo <span className="text-gray-400">(Optional)</span>
            </label>
            <div className="flex items-start gap-4">
              <div className="w-20 h-20 rounded-full overflow-hidden bg-gray-200 flex-shrink-0">
                {logoLoading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  </div>
                )}
                {formData.logo ? (
                  <img
                    src={formData?.logo?.fileUrl}
                    alt="Logo preview"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-400">
                    <svg
                      width="32"
                      height="32"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15M17 8L12 3M12 3L7 8M12 3V15"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                )}
                {logoLoading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  </div>
                )}
              </div>
              <div className="flex-1">
                <input
                  type="file"
                  id="logo-upload"
                  accept="image/png, image/jpeg, image/jpg"
                  onChange={handleLogoChange}
                  className="hidden"
                />
                <label
                  htmlFor="logo-upload"
                  className={`inline-block px-4 py-2 bg-lightbg font-medium text-sm rounded-lg cursor-pointer transition-colors ${
                    errors.logo
                      ? "text-red-600 bg-red-50 hover:bg-red-100"
                      : "text-primary hover:bg-primary/10"
                  }`}
                >
                  Choose File
                </label>
                <p className="text-sm font-normal text-textcolor tracking-[0] leading-[normal] mt-2">
                  Format: png, jpg (Max size: 5MB)
                </p>
                <ErrorMessage message={errors.logo} />
              </div>
            </div>
          </div>

          {/* Club Name */}
          <div>
            <label
              htmlFor="clubName"
              className="block text-base font-medium text-black tracking-[0] leading-[normal] mb-2"
            >
              Club Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="clubName"
              value={formData.clubName}
              onChange={(e) => handleInputChange("clubName", e.target.value)}
              className={`w-full px-4 py-3 bg-white rounded-full text-base font-normal text-black border transition-colors ${
                errors.clubName
                  ? "border-red-500 focus:border-red-500"
                  : "border-gray-200 focus:border-primary"
              } focus:outline-none`}
              placeholder="Enter club name"
            />
            <ErrorMessage message={errors.clubName} />
          </div>
              <div>
            <label
              htmlFor="clubName"
              className="block text-base font-medium text-black tracking-[0] leading-[normal] mb-2"
            >
              Club Tagline <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="clubName"
              value={formData?.clubTagline}
              onChange={(e) => handleInputChange("clubTagline", e.target.value)}
              className={`w-full px-4 py-3 bg-white rounded-full text-base font-normal text-black border transition-colors ${
                errors.clubTagline
                  ? "border-red-500 focus:border-red-500"
                  : "border-gray-200 focus:border-primary"
              } focus:outline-none`}
              placeholder="Enter club tagline"
            />
            <ErrorMessage message={errors.clubTagline} />
          </div>

          {/* Description */}
          <div>
            <label
              htmlFor="description"
              className="block text-base font-medium text-black tracking-[0] leading-[normal] mb-2"
            >
              Description <span className="text-red-500">*</span>
            </label>
            <textarea
              id="description"
              value={formData.description}
              onChange={(e) => handleInputChange("description", e.target.value)}
              rows="4"
              className={`w-full px-4 py-3 bg-white rounded-xl text-base font-normal text-black border transition-colors resize-none ${
                errors.description
                  ? "border-red-500 focus:border-red-500"
                  : "border-gray-200 focus:border-primary"
              } focus:outline-none`}
              placeholder="Enter description"
            />
            <ErrorMessage message={errors.description} />
          </div>

          {/* Actions */}
          <div className="flex items-center justify-center gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="px-6 py-3 bg-white border flex-1 max-w-[265px]  border-primary text-primary font-medium text-base rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || hasErrors}
              className="px-6 py-3 bg-primary hover:bg-hover flex-1 max-w-[265px]  disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-medium text-base rounded-full text-center transition-colors justify-center flex items-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Saving...
                </>
              ) : (
                "Save"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditBasicInfoModal;
